import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

export default function CDP() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [warmups, setWarmups] = useState([]);
  const [warmupsPassed, setWarmupsPassed] = useState(false);
  const [passedCount, setPassedCount] = useState(0);
  const [totalDocs, setTotalDocs] = useState(0);
  const [certLevel, setCertLevel] = useState(null);
  const [activeEnrolments, setActiveEnrolments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(null);
  const [noAccess, setNoAccess] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const { data } = await api.get('/cdp/status');
      setWarmups(data.warmups || []);
      setWarmupsPassed(data.warmupsPassed || false);
      setItems(data.items || []);
      setPassedCount(data.passedCount);
      setTotalDocs(data.totalDocs);
      setCertLevel(data.certLevel);
      setActiveEnrolments(data.activeEnrolments || []);
    } catch (e) {
      if (e.response?.status === 403) setNoAccess(true);
    }
    finally { setLoading(false); }
  };

  const handleStart = async (item) => {
    // Check if there's an active enrolment for this doc
    const active = activeEnrolments.find(e =>
      (e.documentId?._id || e.documentId) === item.docId
    );
    if (active) { navigate(`/cdp/doc/${active._id}`); return; }
    // For passed items, load history and navigate to last passed enrolment
    if (item.passed) {
      try {
        const { data } = await api.get(`/cdp/documents/${item.docId}/history`);
        const passedEn = data.enrolments?.find(e => e.status === 'passed');
        if (passedEn) { navigate(`/cdp/doc/${passedEn._id}`); return; }
      } catch { }
      return;
    }
    setStarting(item.docId);
    try {
      const { data } = await api.post(`/cdp/documents/${item.docId}/enrol`);
      navigate(`/cdp/doc/${data.enrolment._id}`);
    } catch (e) { alert(e.response?.data?.error || 'Failed to enrol'); }
    finally { setStarting(null); }
  };

  const progressPct = totalDocs > 0 ? Math.round((passedCount / totalDocs) * 100) : 0;

  if (loading) return <div style={st.center}><div style={st.spin} /></div>;

  if (noAccess) return (
    <div style={st.page} className="page-content">
      <div style={{ textAlign: 'center', marginTop: '60px' }}>
        <h1 style={st.h1}>Commercial Drafting Programme</h1>
        <p style={{ fontSize: '14px', color: '#6b7280', lineHeight: '1.6', marginTop: '12px' }}>
          You do not have access to the Drafting Programme yet.<br />Please contact the administrator to enable access for your account.
        </p>
        <button onClick={() => navigate('/')} style={{ background: '#0c2340', color: '#fff', border: 'none', padding: '11px 22px', borderRadius: '10px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', marginTop: '16px' }}>Back to Dashboard</button>
      </div>
    </div>
  );

  return (
    <div style={st.page} className="page-content">
      <div style={{ textAlign: 'center', marginBottom: '28px' }}>
        <p style={st.eye}>Commercial Drafting Programme</p>
        <h1 style={st.h1}>Certified Practice Session</h1>
        <p style={st.sub}>Work with seven professionally drafted templates. Apply facts to each template. Score 75% to pass.</p>
      </div>

      <div style={st.quote}>
        <p style={st.qt}>"A well-drafted document is not just a legal instrument — it is a window into the lawyer's mind. It tells the reader how clearly you think, how carefully you listen, and how deeply you understand the business you are serving."</p>
        <p style={st.qs}>— Nitin Potdar, Senior Corporate Lawyer, Mumbai</p>
      </div>

      {/* How it works */}
      <div style={st.howSection}>
        <h2 style={{ fontSize: '16px', fontWeight: '700', color: '#0c2340', marginBottom: '12px' }}>How It Works</h2>
        {[
          { step: '1', title: 'Choose a Document', desc: 'Pick any of the seven documents. No fixed order.' },
          { step: '2', title: 'Receive Your Brief & Template', desc: 'You get a unique fact scenario and a Word template to download.' },
          { step: '3', title: 'Draft and Submit', desc: 'Apply the facts to the template. Upload your completed .docx within 7 days.' },
          { step: '4', title: 'Get Assessed', desc: 'Your draft is assessed on a 100-point Rubric. Score 75% to pass.' },
          { step: '5', title: 'Earn Your Certificate', desc: 'Pass 4 for Completion, 6 for Silver, all 7 for Gold.' },
        ].map(s => (
          <div key={s.step} style={{ display: 'flex', gap: '12px', marginBottom: '10px', alignItems: 'flex-start' }}>
            <span style={st.stepNum}>{s.step}</span>
            <div>
              <p style={{ fontSize: '13px', fontWeight: '700', color: '#0c2340', margin: '0 0 2px' }}>{s.title}</p>
              <p style={{ fontSize: '12px', color: '#6b7280', margin: 0, lineHeight: '1.5' }}>{s.desc}</p>
            </div>
          </div>
        ))}
      </div>

      {/* How to Start */}
      <div style={st.howSection}>
        <h2 style={{ fontSize: '16px', fontWeight: '700', color: '#0c2340', marginBottom: '10px' }}>How to Start</h2>
        <p style={{ fontSize: '13px', color: '#374151', lineHeight: '1.75', margin: 0 }}>
          Once you select a document, you will receive a Brief Statement of Facts and a Word template. Download the Template to your own laptop or computer. Read the facts carefully, fill in the relevant details and complete the draft. The Template is a comprehensive document so may need a good editing or even deletions of some clauses wholly or partially — do it freely. Don't worry.
        </p>
        <p style={{ fontSize: '13px', color: '#dc2626', lineHeight: '1.75', margin: '10px 0 0' }}>
          Where the facts do not specify an address or other details, simply use a fictional simple ones — the assessment looks at your drafting, not the accuracy of the address.
        </p>
        <p style={{ fontSize: '13px', color: '#374151', lineHeight: '1.75', margin: '10px 0 0' }}>
          Before submitting, read through your draft once more — a second read always catches something. Then upload your completed document for assessment. Within minutes you will receive a detailed assessment with specific observations and suggested corrections. Carry out those corrections and resubmit. Score 75% or above and you move straight to your next document. It is much simpler than you think.
        </p>
      </div>

      {/* Progress bar */}
      <div style={st.progressWrap}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
          <span style={{ fontSize: '13px', fontWeight: '700', color: '#0c2340' }}>Progress</span>
          <span style={{ fontSize: '12px', color: '#6b7280' }}>{passedCount} of {totalDocs} documents passed</span>
        </div>
        <div style={st.progressBar}>
          <div style={{ ...st.progressFill, width: `${progressPct}%` }} />
        </div>
        <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
          {[
            { n: 4, label: 'Certificate of Completion', color: '#d97706' },
            { n: 6, label: 'Silver Certificate', color: '#6b7280' },
            { n: totalDocs, label: 'Gold Certificate', color: '#b45309' },
          ].map(tier => (
            <span key={tier.label} style={{
              fontSize: '11px', fontWeight: '600', padding: '3px 10px', borderRadius: '99px',
              background: passedCount >= tier.n ? tier.color : '#f3f4f6',
              color: passedCount >= tier.n ? '#fff' : '#9ca3af',
            }}>{tier.label} ({tier.n})</span>
          ))}
        </div>
      </div>

      {/* Warm-up section */}
      {warmups.length > 0 && (
        <div style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
            <h2 style={{ fontSize: '16px', fontWeight: '700', color: '#0c2340', margin: 0 }}>Warm-Up Sessions</h2>
            {warmupsPassed && <span style={{ fontSize: '11px', fontWeight: '600', padding: '3px 10px', borderRadius: '99px', background: '#d1fae5', color: '#065f46' }}>Completed</span>}
          </div>
          {!warmupsPassed && (
            <p style={{ fontSize: '13px', color: '#374151', lineHeight: '1.6', marginBottom: '12px' }}>
              Complete both warm-up exercises to unlock the main programme. They use real templates with a simple brief. The goal is to build your confidence.
            </p>
          )}
          <div style={st.grid}>
            {warmups.map(item => (
              <Card key={item.docId} item={item} starting={starting} onStart={handleStart} activeEnrolments={activeEnrolments} isWarmup />
            ))}
          </div>
        </div>
      )}

      {/* Main programme heading */}
      {warmups.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
          <h2 style={{ fontSize: '16px', fontWeight: '700', color: '#0c2340', margin: 0 }}>Main Programme</h2>
          {!warmupsPassed && <span style={{ fontSize: '11px', fontWeight: '600', padding: '3px 10px', borderRadius: '99px', background: '#fef3c7', color: '#92400e' }}>Complete warm-ups to unlock</span>}
        </div>
      )}

      {/* Document grid */}
      <div style={st.grid}>
        {items.map(item => (
          <Card key={item.docId} item={{ ...item, locked: item.locked && !warmupsPassed }} starting={starting} onStart={handleStart} activeEnrolments={activeEnrolments} />
        ))}
      </div>

      {/* Certificate banner */}
      {certLevel && (
        <div style={st.cert}>
          <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#065f46', margin: '0 0 6px' }}>
            {certLevel === 'gold' ? 'Gold Certificate Earned!' : certLevel === 'silver' ? 'Silver Certificate Earned!' : 'Certificate of Completion Earned!'}
          </h3>
          <p style={{ fontSize: '13px', color: '#065f46', marginBottom: '12px' }}>
            You have passed {passedCount} out of {totalDocs} documents.
          </p>
          <button onClick={() => navigate('/cdp/certificate')} style={st.cbtn}>View Certificate</button>
        </div>
      )}

    </div>
  );
}

function Card({ item, starting, onStart, activeEnrolments, isWarmup }) {
  const active = activeEnrolments.find(e => (e.documentId?._id || e.documentId) === item.docId);
  const locked = item.locked;
  const label = locked ? 'Locked' : item.passed ? 'View' : active ? 'Continue' : 'Start';
  const bg = locked ? '#f3f4f6' : item.passed ? '#d1fae5' : active ? '#dbeafe' : '#f0f9ff';
  const fg = locked ? '#9ca3af' : item.passed ? '#065f46' : active ? '#1e40af' : '#0c2340';
  const isStarting = starting === item.docId;

  return (
    <div
      onClick={() => !locked && onStart(item)}
      style={{
        ...st.card,
        cursor: locked ? 'default' : 'pointer',
        opacity: locked ? 0.55 : 1,
        ...(item.passed ? { background: '#f0fdf4', borderColor: '#bbf7d0' } : {}),
        ...(isWarmup && !item.passed ? { borderColor: '#fbbf24', borderWidth: '1.5px' } : {}),
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <span style={{ fontSize: '24px', fontWeight: '800', color: item.passed ? 'rgba(6,95,70,0.15)' : 'rgba(12,35,64,0.08)' }}>
          {isWarmup ? 'W' + item.order : item.order}
        </span>
        <span style={{ background: bg, color: fg, padding: '3px 10px', borderRadius: '99px', fontSize: '11px', fontWeight: '600' }}>
          {isStarting ? 'Enrolling...' : label}
        </span>
      </div>
      <h3 style={{ fontSize: '14px', fontWeight: '700', color: locked ? '#9ca3af' : item.passed ? '#065f46' : '#0c2340', margin: 0 }}>{item.title}</h3>
      {item.description && !item.passed && !locked && (
        <p style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>{item.description}</p>
      )}
      {item.passed && <p style={{ fontSize: '11px', color: '#065f46', marginTop: '4px', fontWeight: '600' }}>Completed</p>}
    </div>
  );
}

const st = {
  page: { maxWidth: '800px', margin: '0 auto', padding: '32px 20px 60px' },
  center: { display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 'calc(100vh - 60px)' },
  spin: { width: '28px', height: '28px', borderRadius: '50%', border: '2.5px solid #e5e7eb', borderTop: '2.5px solid #0c2340', animation: 'spin 0.8s linear infinite' },
  eye: { fontSize: '11px', fontWeight: '700', letterSpacing: '2.5px', textTransform: 'uppercase', color: '#6b7280', marginBottom: '6px' },
  h1: { fontFamily: "'DM Sans',sans-serif", fontSize: '26px', fontWeight: '800', color: '#0c2340', margin: '0 0 6px' },
  sub: { fontSize: '14px', color: '#6b7280', lineHeight: '1.6' },
  quote: { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '14px', padding: '22px', marginBottom: '24px', textAlign: 'center' },
  qt: { fontSize: '13px', fontStyle: 'italic', color: '#0c2340', lineHeight: '1.7', margin: '0 0 6px' },
  qs: { fontSize: '12px', fontWeight: '600', color: '#6b7280', margin: 0 },
  progressWrap: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px', padding: '18px', marginBottom: '24px' },
  progressBar: { height: '8px', background: '#e5e7eb', borderRadius: '99px', overflow: 'hidden' },
  progressFill: { height: '100%', background: 'linear-gradient(90deg, #0c2340, #1e40af)', borderRadius: '99px', transition: 'width 0.5s ease' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))', gap: '12px', marginBottom: '24px' },
  card: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '18px', transition: 'transform .15s' },
  cert: { background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '14px', padding: '24px', textAlign: 'center', marginBottom: '24px' },
  cbtn: { background: '#065f46', color: '#fff', border: 'none', padding: '10px 22px', borderRadius: '10px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
  howSection: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px', padding: '22px' },
  stepNum: { width: '24px', height: '24px', borderRadius: '50%', background: '#0c2340', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '800', flexShrink: 0 },
};
